// Chakra imports
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Link,
  Text,
  useColorModeValue,
  useToast,
  Alert,
  AlertIcon,
  Select,
  CheckboxGroup,
  Checkbox,
  Stack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
} from "@chakra-ui/react";
// Assets
import BgSignUp from "assets/img/BgSignUp.png";
import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { useAuth } from "contexts/AuthContext";
import { findCandidateByNameAndPhone } from "utils/firestoreUtils";
import jobTypes from "constants/jobTypes";

function SignUp() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    passwordConfirm: "",
    userType: "",
    preferredJobs: [],
    availableDays: [],
    availableTimeSlots: [],
    termsAgreed: false,
    notificationConsent: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [candidateData, setCandidateData] = useState(null);
  const [mergeMode, setMergeMode] = useState(false);

  const { signup } = useAuth();
  const history = useHistory();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const titleColor = useColorModeValue("teal.300", "teal.200");
  const textColor = useColorModeValue("gray.700", "white");
  const bgColor = useColorModeValue("white", "gray.700");

  const days = ['월', '화', '수', '목', '금', '토', '일'];
  const timeSlots = ['09-13', '13-17', '17-21', '21-24'];

  function handleChange(field, value) {
    setFormData(prev => ({ ...prev, [field]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();

    if (formData.password !== formData.passwordConfirm) {
      return setError("비밀번호가 일치하지 않습니다.");
    }

    if (formData.password.length < 6) {
      return setError("비밀번호는 최소 6자 이상이어야 합니다.");
    }

    if (!formData.userType) {
      return setError("사용자 유형을 선택해주세요.");
    }

    if (!formData.termsAgreed) {
      return setError("필수 약관에 동의해주세요.");
    }

    try {
      setError("");
      setLoading(true);
      
      // 구인풀에 등록된 회원인지 확인
      const existingCandidate = await findCandidateByNameAndPhone(
        formData.name,
        formData.phone
      );

      if (existingCandidate) {
        // 구인풀에 등록된 회원이면 팝업으로 확인
        setCandidateData(existingCandidate);
        setLoading(false);
        onOpen();
        return;
      }

      // 일반 회원가입 진행
      await proceedSignup(false);
      
    } catch (error) {
      console.error("회원가입 오류:", error);
      handleSignupError(error);
    } finally {
      setLoading(false);
    }
  }

  async function proceedSignup(mergeWithCandidate) {
    try {
      setError("");
      setLoading(true);
      
      let profileData = {
        name: formData.name,
        phone: formData.phone,
        userType: formData.userType,
        preferredJobs: formData.preferredJobs,
        availableDays: formData.availableDays,
        availableTimeSlots: formData.availableTimeSlots,
        notificationConsent: formData.notificationConsent
      };

      // 구인풀 데이터와 병합
      if (mergeWithCandidate && candidateData) {
        profileData = {
          ...profileData,
          // 구인풀 데이터 우선 적용 (사용자가 입력하지 않은 경우)
          userType: formData.userType || candidateData.userType,
          preferredJobs: formData.preferredJobs.length > 0 
            ? formData.preferredJobs 
            : candidateData.preferredJobs || [],
          availableDays: formData.availableDays.length > 0 
            ? formData.availableDays 
            : candidateData.availableDays || [],
          notes: candidateData.notes || '',
          // 구인풀에 이미 등록되어 있었음을 표시
          mergedFromCandidatePool: true,
          candidatePoolId: candidateData.id
        };
      }

      await signup(formData.email, formData.password, profileData);
      
      toast({
        title: "회원가입 성공",
        description: mergeWithCandidate 
          ? "구인풀 정보가 병합되었습니다. 환영합니다! 로그인해주세요." 
          : "환영합니다! 로그인해주세요.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      
      history.push("/auth/signin");
    } catch (error) {
      console.error("회원가입 오류:", error);
      handleSignupError(error);
    } finally {
      setLoading(false);
      onClose();
    }
  }

  function handleSignupError(error) {
    let errorMessage = "회원가입에 실패했습니다.";
    
    if (error.code === 'auth/email-already-in-use') {
      errorMessage = "이미 사용 중인 이메일입니다.";
    } else if (error.code === 'auth/invalid-email') {
      errorMessage = "유효하지 않은 이메일 형식입니다.";
    } else if (error.code === 'auth/weak-password') {
      errorMessage = "비밀번호가 너무 약합니다.";
    }
    
    setError(errorMessage);
  }

  function handleMergeConfirm() {
    proceedSignup(true);
  }

  function handleMergeCancel() {
    onClose();
    proceedSignup(false);
  }

  return (
    <Flex
      direction='column'
      alignSelf='center'
      justifySelf='center'
      overflow='hidden'>
      <Box
        position='absolute'
        minH={{ base: "70vh", md: "50vh" }}
        w={{ md: "calc(100vw - 50px)" }}
        borderRadius={{ md: "15px" }}
        left='0'
        right='0'
        bgRepeat='no-repeat'
        overflow='hidden'
        zIndex='-1'
        top='0'
        bgImage={BgSignUp}
        bgSize='cover'
        mx={{ md: "auto" }}
        mt={{ md: "14px" }}></Box>
      <Flex
        direction='column'
        textAlign='center'
        justifyContent='center'
        align='center'
        mt='6.5rem'
        mb='30px'>
        <Text fontSize='4xl' color='white' fontWeight='bold'>
          부산대학교 소비자생활협동조합 구인 풀 등록
        </Text>
        <Text
          fontSize='md'
          color='white'
          fontWeight='normal'
          mt='10px'
          mb='26px'
          w={{ base: "90%", sm: "60%", lg: "40%", xl: "30%" }}>
          구인 풀 등록 후에 각종 구인 안내를 받을 수 있습니다.
        </Text>
      </Flex>
      <Flex alignItems='center' justifyContent='center' mb='60px' mt='20px'>
        <Flex
          direction='column'
          w='600px'
          background='transparent'
          borderRadius='15px'
          p='40px'
          mx={{ base: "20px" }}
          bg={bgColor}
          boxShadow='0 20px 27px 0 rgb(0 0 0 / 5%)'>
          <Text
            fontSize='xl'
            color={textColor}
            fontWeight='bold'
            textAlign='center'
            mb='22px'>
            구인 풀 가입
          </Text>

          {error && (
            <Alert status='error' mb='24px' borderRadius='15px'>
              <AlertIcon />
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <FormControl>
              <FormLabel ms='4px' fontSize='sm' fontWeight='normal'>
                이름 *
              </FormLabel>
              <Input
                fontSize='sm'
                ms='4px'
                borderRadius='15px'
                type='text'
                placeholder='홍길동'
                mb='24px'
                size='lg'
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                required
              />

              <FormLabel ms='4px' fontSize='sm' fontWeight='normal'>
                이메일 *
              </FormLabel>
              <Input
                fontSize='sm'
                ms='4px'
                borderRadius='15px'
                type='email'
                placeholder='email@example.com'
                mb='24px'
                size='lg'
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                required
              />


              <FormLabel ms='4px' fontSize='sm' fontWeight='normal'>
                비밀번호 *
              </FormLabel>
              <Input
                fontSize='sm'
                ms='4px'
                borderRadius='15px'
                type='password'
                placeholder='최소 6자 이상'
                mb='24px'
                size='lg'
                value={formData.password}
                onChange={(e) => handleChange('password', e.target.value)}
                required
              />

              <FormLabel ms='4px' fontSize='sm' fontWeight='normal'>
                비밀번호 확인 *
              </FormLabel>
              <Input
                fontSize='sm'
                ms='4px'
                borderRadius='15px'
                type='password'
                placeholder='비밀번호 재입력'
                mb='24px'
                size='lg'
                value={formData.passwordConfirm}
                onChange={(e) => handleChange('passwordConfirm', e.target.value)}
                required
              />

              <FormLabel ms='4px' fontSize='sm' fontWeight='normal'>
                전화번호 *
              </FormLabel>
              <Input
                fontSize='sm'
                ms='4px'
                borderRadius='15px'
                type='tel'
                placeholder='010-1234-5678'
                mb='24px'
                size='lg'
                value={formData.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                required
              />

              <FormLabel ms='4px' fontSize='sm' fontWeight='normal'>
                사용자 유형 *
              </FormLabel>
              <Select
                fontSize='sm'
                ms='4px'
                borderRadius='15px'
                mb='24px'
                size='lg'
                placeholder='선택하세요'
                value={formData.userType}
                onChange={(e) => handleChange('userType', e.target.value)}
                required
              >
                <option value="학생">학생</option>
                <option value="기타">기타</option>
              </Select>

              <FormLabel ms='4px' fontSize='sm' fontWeight='normal'>
                희망 근무 유형
              </FormLabel>
              <CheckboxGroup
                value={formData.preferredJobs}
                onChange={(values) => handleChange('preferredJobs', values)}
              >
                <Stack spacing={2} direction='row' flexWrap='wrap' mb='24px'>
                  {jobTypes.map(type => (
                    <Checkbox key={type} value={type}>{type}</Checkbox>
                  ))}
                </Stack>
              </CheckboxGroup>

              <FormLabel ms='4px' fontSize='sm' fontWeight='normal'>
                근무 가능 요일
              </FormLabel>
              <CheckboxGroup
                value={formData.availableDays}
                onChange={(values) => handleChange('availableDays', values)}
              >
                <Stack spacing={2} direction='row' flexWrap='wrap' mb='24px'>
                  {days.map(day => (
                    <Checkbox key={day} value={day}>{day}</Checkbox>
                  ))}
                </Stack>
              </CheckboxGroup>

              <FormLabel ms='4px' fontSize='sm' fontWeight='normal'>
                근무 가능 시간대
              </FormLabel>
              <CheckboxGroup
                value={formData.availableTimeSlots}
                onChange={(values) => handleChange('availableTimeSlots', values)}
              >
                <Stack spacing={2} direction='row' flexWrap='wrap' mb='24px'>
                  {timeSlots.map(slot => (
                    <Checkbox key={slot} value={slot}>{slot}시</Checkbox>
                  ))}
                </Stack>
              </CheckboxGroup>

              <Box
                bg='gray.50'
                p='20px'
                borderRadius='15px'
                mb='24px'
                fontSize='sm'
              >
                <Checkbox
                  isChecked={formData.termsAgreed}
                  onChange={(e) => handleChange('termsAgreed', e.target.checked)}
                  colorScheme='teal'
                  fontWeight='bold'
                  mb='12px'
                  isRequired
                >
                  [필수] 홈페이지 이용약관 및 개인정보 수집·이용에 동의합니다.
                </Checkbox>
                
                <Box ml='24px' color='gray.600' fontSize='xs' mb='12px'>
                  <Text fontWeight='bold' mb='8px'>개인정보 수집·이용 동의</Text>
                  <Text mb='4px'>소비자생활협동조합 구인 시스템(이하 "서비스")은 관련 법령에 따라 회원 가입 및 서비스 이용을 위해 아래의 개인정보를 수집·이용합니다.</Text>
                  <Text fontWeight='bold' mt='8px'>1. 수집 항목</Text>
                  <Text>이름, 전화번호, 이메일, 소속(캠퍼스/매장), 희망 근무 정보, 서비스 이용기록</Text>
                  <Text fontWeight='bold' mt='4px'>2. 수집 목적</Text>
                  <Text>회원 식별 및 서비스 제공, 구인 공고·신청·선정 내역 관리, 이용자 본인 확인 및 고지사항 전달</Text>
                  <Text fontWeight='bold' mt='4px'>3. 보유·이용 기간</Text>
                  <Text>회원 탈퇴 시 또는 관련 법령에 따라 보존 필요 기간 종료 시까지</Text>
                  <Text fontWeight='bold' mt='4px'>4. 이용자의 권리</Text>
                  <Text>이용자는 개인정보 제공을 거부할 권리가 있으며, 개인정보 제공을 거부할 경우 회원가입 및 서비스 이용이 제한될 수 있습니다.</Text>
                </Box>

                <Checkbox
                  isChecked={formData.notificationConsent}
                  onChange={(e) => handleChange('notificationConsent', e.target.checked)}
                  colorScheme='teal'
                  fontWeight='bold'
                  mb='8px'
                >
                  [선택] 구인·구직 알림톡/문자/이메일 수신에 동의합니다.
                </Checkbox>
                
                <Box ml='24px' color='gray.600' fontSize='xs'>
                  <Text mb='4px'>서비스는 회원에게 구인 공고, 근무 요청, 행사 인력 모집 등의 안내를 위해 카카오 알림톡, 문자, 이메일 등의 수단으로 메시지를 발송할 수 있습니다. 고객이 1회 요청 했더라도 해당 조건에 맞는 여러 서비스 및 모집이 안내될 수 있습니다.</Text>
                  <Text>본 동의는 선택 사항이며, 미동의 시 서비스 이용은 가능하나 일부 구인 공고 알림 및 개별 안내 수신이 제한될 수 있습니다.</Text>
                  <Text mt='4px'>동의 철회는 마이페이지 또는 고객센터를 통해 언제든지 가능합니다.</Text>
                </Box>
              </Box>

              <Button
                type='submit'
                bg='teal.300'
                fontSize='10px'
                color='white'
                fontWeight='bold'
                w='100%'
                h='45'
                mb='24px'
                isLoading={loading}
                loadingText='가입 중...'
                _hover={{
                  bg: "teal.200",
                }}
                _active={{
                  bg: "teal.400",
                }}>
                회원가입
              </Button>
            </FormControl>
          </form>
          
          <Flex
            flexDirection='column'
            justifyContent='center'
            alignItems='center'
            maxW='100%'
            mt='0px'>
            <Text color={textColor} fontWeight='medium'>
              이미 계정이 있으신가요?
              <Link
                color={titleColor}
                as='span'
                ms='5px'
                fontWeight='bold'
                onClick={() => history.push('/auth/signin')}
                cursor='pointer'>
                로그인
              </Link>
            </Text>
          </Flex>
        </Flex>
      </Flex>

      {/* 구인풀 등록 확인 모달 */}
      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>구인풀 등록 회원 확인</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Alert status="info" borderRadius="md" mb={4}>
              <AlertIcon />
              <Box>
                <Text fontWeight="bold">구인풀에 등록되어 있는 회원입니다!</Text>
              </Box>
            </Alert>
            <Text mb={4}>
              <strong>{formData.name}</strong>님은 관리자가 이미 구인 풀에 등록한 회원입니다.
            </Text>
            <Text mb={4}>
              구인풀에 저장된 정보(희망업무, 가능요일 등)를 회원 정보에 반영하시겠습니까?
            </Text>
            {candidateData && (
              <Box bg="gray.50" p={4} borderRadius="md" fontSize="sm">
                <Text><strong>유형:</strong> {candidateData.userType || '-'}</Text>
                <Text><strong>희망업무:</strong> {candidateData.preferredJobs?.join(', ') || '-'}</Text>
                <Text><strong>가능요일:</strong> {candidateData.availableDays?.join(', ') || '-'}</Text>
                {candidateData.notes && (
                  <Text><strong>메모:</strong> {candidateData.notes}</Text>
                )}
              </Box>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={handleMergeCancel}>
              아니오, 새로 등록
            </Button>
            <Button colorScheme="blue" onClick={handleMergeConfirm} isLoading={loading}>
              예, 정보 반영
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Flex>
  );
}

export default SignUp;
